import React from "react";

import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import { makeStyles } from "@material-ui/core/styles";
import BusinessRoundedIcon from "@material-ui/icons/BusinessRounded";
import CheckCircleRoundedIcon from "@material-ui/icons/CheckCircleRounded";
import PhoneAndroidRoundedIcon from "@material-ui/icons/PhoneAndroidRounded";
import StarsRoundedIcon from "@material-ui/icons/StarsRounded";


import { useSetup } from "../hooks/useSetup";
import api from "../../../services/api";

import {
  KoraxOnboardingShell,
  KoraxOnboardingContent,
  KoraxOnboardingFooter
} from "../../../components/KoraxUI/Onboarding";

const useStyles = makeStyles(theme => ({
  content: {
    width: "100%",
    maxWidth: 900
  },

  hero: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center"
  },

  successIcon: {
    width: 76,
    height: 76,
    marginBottom: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 24,
    color: "#FFFFFF",
    background: "linear-gradient(135deg,#155EEF,#123E9C)",
    boxShadow: "0 16px 36px rgba(21,94,239,.24)"
  },

  successIconSvg: {
    fontSize: 38
  },

  title: {
    color: theme.palette.text.primary,
    fontSize: 30,
    fontWeight: 900,
    lineHeight: 1.2,

    [theme.breakpoints.down("sm")]: {
      fontSize: 25
    }
  },

  description: {
    maxWidth: 650,
    marginTop: 10,
    color: theme.palette.text.secondary,
    fontSize: 13.5,
    lineHeight: 1.65
  },

  highlight: {
    color: "#155EEF",
    fontWeight: 800
  },

  summary: {
    marginTop: 30,
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 14,

    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },

  summaryCard: {
    minWidth: 0,
    padding: 18,
    borderRadius: 15,
    border: `1px solid ${theme.palette.divider}`,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.025)"
        : "#FFFFFF"
  },

  summaryIcon: {
    width: 40,
    height: 40,
    marginBottom: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 12,
    color: "#155EEF",
    background: "rgba(21,94,239,.10)"
  },

  summaryIconSvg: {
    fontSize: 21
  },

  summaryLabel: {
    color: theme.palette.text.secondary,
    fontSize: 10.5,
    fontWeight: 800,
    letterSpacing: ".06em",
    textTransform: "uppercase"
  },

  summaryValue: {
    marginTop: 6,
    overflow: "hidden",
    color: theme.palette.text.primary,
    fontSize: 14,
    fontWeight: 800,
    lineHeight: 1.4,
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },

  checklist: {
    marginTop: 18,
    padding: 20,
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.025)"
        : "#FFFFFF"
  },

  checklistTitle: {
    marginBottom: 15,
    color: theme.palette.text.primary,
    fontSize: 15,
    fontWeight: 800
  },

  checklistGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 11,

    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },

  checklistItem: {
    padding: 12,
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    borderRadius: 11,
    background:
      theme.palette.type === "dark"
        ? "rgba(0,0,0,.12)"
        : "#F8FAFC"
  },

  checkIcon: {
    marginTop: 1,
    flexShrink: 0,
    color: "#16A34A",
    fontSize: 20
  },

  itemTitle: {
    color: theme.palette.text.primary,
    fontSize: 11.5,
    fontWeight: 800,
    lineHeight: 1.35
  },

  itemText: {
    marginTop: 3,
    color: theme.palette.text.secondary,
    fontSize: 10.5,
    lineHeight: 1.45
  },

  journeyCard: {
    marginTop: 18,
    padding: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 18,
    borderRadius: 16,
    border: "1px solid rgba(21,94,239,.22)",
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(135deg,rgba(21,94,239,.13),rgba(21,94,239,.04))"
        : "linear-gradient(135deg,rgba(21,94,239,.08),rgba(255,255,255,1))",

    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch"
    }
  },

  journeyContent: {
    minWidth: 0
  },

  journeyTitle: {
    color: theme.palette.text.primary,
    fontSize: 15,
    fontWeight: 900
  },

  journeyText: {
    maxWidth: 590,
    marginTop: 6,
    color: theme.palette.text.secondary,
    fontSize: 11.5,
    lineHeight: 1.55
  },

  journeyButton: {
    flexShrink: 0,
    padding: "11px 18px",
    borderRadius: 11,
    color: "#FFFFFF",
    fontSize: 11.5,
    fontWeight: 800,
    textTransform: "none",
    background: "linear-gradient(135deg,#155EEF,#123E9C)",
    boxShadow: "0 10px 24px rgba(21,94,239,.20)",

    "&:hover": {
      background: "linear-gradient(135deg,#124FD0,#0F347F)"
    }
  },

  note: {
    marginTop: 18,
    padding: 15,
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    borderRadius: 13,
    border: "1px solid rgba(21,94,239,.16)",
    background:
      theme.palette.type === "dark"
        ? "rgba(21,94,239,.08)"
        : "rgba(21,94,239,.045)"
  },

  noteIcon: {
    flexShrink: 0,
    color: "#155EEF",
    fontSize: 21
  },

  noteText: {
    color: theme.palette.text.secondary,
    fontSize: 11.5,
    lineHeight: 1.55
  }
}));

export default function FinishStep() {
  const classes = useStyles();

  const {
    company,
    whatsapp,
    ai,
    previousStep
  } = useSetup();

  const companyName =
    company?.name ||
    company?.companyName ||
    "Sua empresa";

  const phoneNumber =
    whatsapp?.number ||
    whatsapp?.phone ||
    "WhatsApp conectado";

  const objectiveLabels = {
    vendas: "Vender mais",
    atendimento: "Atender clientes",
    agendamento: "Agendar serviços",
    suporte: "Dar suporte"
  };

  const toneLabels = {
    humanizado: "Humanizada",
    consultivo: "Consultiva",
    objetivo: "Objetiva"
  };

  const objective =
    objectiveLabels[ai?.objective] ||
    "Atendimento inteligente";

  const tone =
    toneLabels[ai?.tone] ||
    "Humanizada";

  const checklist = [
    {
      title: "Empresa configurada",
      text: "Informações principais registradas."
    },
    {
      title: "WhatsApp preparado",
      text: "Canal pronto para receber clientes."
    },
    {
      title: "Objetivo definido",
      text: objective
    },
    {
      title: "Personalidade selecionada",
      text: `Comunicação ${tone}.`
    },
    {
      title: "Base inicial criada",
      text: "Dados do negócio prontos para evolução."
    },
    {
      title: "Teste realizado",
      text: "Experiência inicial validada com sucesso."
    }
  ];

  const handleEnterPlatform = async () => {
    await api.post("/onboarding/finish", {
      promptId: Number(ai?.promptId || 0),
      whatsappId: Number(whatsapp?.connectionId || 0),
      crmObjective: ai?.objective || "vendas"
    });
    window.location.replace("/tickets");
  };

  const handleCreateJourney = async () => {
    try {
      const promptId = Number(ai?.promptId || 0);

      if (!promptId) {
        throw new Error("Prompt do onboarding não encontrado.");
      }

      const journeyPath =
        `/korax-ia/treinamento/${promptId}?experience=journey`;

      sessionStorage.setItem("korax_after_onboarding", journeyPath);
      await api.post("/onboarding/finish", {
      promptId: Number(ai?.promptId || 0),
      whatsappId: Number(whatsapp?.connectionId || 0),
      crmObjective: ai?.objective || "vendas"
    });
      window.location.replace(journeyPath);
    } catch (err) {
      sessionStorage.removeItem("korax_after_onboarding");
      console.error(err);
    }
  };

  return (
    <KoraxOnboardingShell step={7} totalSteps={7}>
      <KoraxOnboardingContent>
        <div className={classes.content}>
          <div className={classes.hero}>
            <div className={classes.successIcon}>
              <CheckCircleRoundedIcon className={classes.successIconSvg} />
            </div>

            <Typography className={classes.title}>
              Sua Korax está pronta para começar
            </Typography>

            <Typography className={classes.description}>
              A configuração inicial da{" "}
              <span className={classes.highlight}>
                {companyName}
              </span>{" "}
              foi concluída. Agora você já pode entrar na plataforma e continuar aprimorando sua operação.
            </Typography>
          </div>

          <div className={classes.summary}>
            <div className={classes.summaryCard}>
              <div className={classes.summaryIcon}>
                <BusinessRoundedIcon className={classes.summaryIconSvg} />
              </div>

              <Typography className={classes.summaryLabel}>
                Empresa
              </Typography>

              <Typography className={classes.summaryValue}>
                {companyName}
              </Typography>
            </div>

            <div className={classes.summaryCard}>
              <div className={classes.summaryIcon}>
                <PhoneAndroidRoundedIcon className={classes.summaryIconSvg} />
              </div>

              <Typography className={classes.summaryLabel}>
                WhatsApp
              </Typography>

              <Typography className={classes.summaryValue}>
                {phoneNumber}
              </Typography>
            </div>

            <div className={classes.summaryCard}>
              <div className={classes.summaryIcon}>
                <StarsRoundedIcon className={classes.summaryIconSvg} />
              </div>

              <Typography className={classes.summaryLabel}>
                Objetivo da IA
              </Typography>

              <Typography className={classes.summaryValue}>
                {objective}
              </Typography>
            </div>
          </div>

          <div className={classes.checklist}>
            <Typography className={classes.checklistTitle}>
              Configuração concluída
            </Typography>

            <div className={classes.checklistGrid}>
              {checklist.map(item => (
                <div
                  key={item.title}
                  className={classes.checklistItem}
                >
                  <CheckCircleRoundedIcon className={classes.checkIcon} />

                  <div>
                    <Typography className={classes.itemTitle}>
                      {item.title}
                    </Typography>

                    <Typography className={classes.itemText}>
                      {item.text}
                    </Typography>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={classes.journeyCard}>
            <div className={classes.journeyContent}>
              <Typography className={classes.journeyTitle}>
                Quer criar uma jornada comercial completa?
              </Typography>

              <Typography className={classes.journeyText}>
                Depois de entrar na Korax, acesse Korax IA, clique em Treinar agente e escolha Desenhar jornada comercial. Assim você poderá criar um passo a passo personalizado para a IA seguir no atendimento.
              </Typography>
            </div>
          </div>

          <div className={classes.note}>
            <StarsRoundedIcon className={classes.noteIcon} />

            <Typography className={classes.noteText}>
              Dentro da plataforma você poderá treinar ainda mais sua IA, adicionar mídias, organizar oportunidades, configurar follow-ups e acompanhar toda a operação pelo WhatsApp.
            </Typography>
          </div>
        </div>
      </KoraxOnboardingContent>

      <KoraxOnboardingFooter
        onBack={previousStep}
        onNext={handleEnterPlatform}
        nextLabel="Entrar na Korax"
      />
    </KoraxOnboardingShell>
  );
}
